// src/utils/subscriptionPlans.js

/**
 * Subscription Plans and Feature Definitions
 * This file defines all available plans, features, and limits for the application
 */

// SMS Add-on Packs (one-time purchases, available on any plan)
export const SMS_PACKS = {
  starter_sms: {
    id: 'starter_sms',
    name: 'Starter SMS',
    credits: 50,
    price: 5,
    stripePriceId: 'price_sms_starter_PLACEHOLDER', // TODO: Replace with real Stripe price ID
  },
  standard_sms: {
    id: 'standard_sms',
    name: 'Standard SMS',
    credits: 200,
    price: 15,
    stripePriceId: 'price_sms_standard_PLACEHOLDER',
  },
  pro_sms: {
    id: 'pro_sms',
    name: 'Pro SMS',
    credits: 500,
    price: 30,
    stripePriceId: 'price_sms_pro_PLACEHOLDER',
  },
  business_sms: {
    id: 'business_sms',
    name: 'Business SMS',
    credits: 1500,
    price: 75,
    stripePriceId: 'price_sms_business_PLACEHOLDER',
  },
};

// Route Optimization Add-on Packs (one-time purchases)
export const ROUTE_PACKS = {
  basic_route: {
    id: 'basic_route',
    name: 'Basic Route Pack',
    credits: 10,
    price: 3,
    stripePriceId: 'price_route_basic_PLACEHOLDER', // TODO: Replace with real Stripe price ID
  },
  pro_route: {
    id: 'pro_route',
    name: 'Pro Route Pack',
    credits: 30,
    price: 8,
    stripePriceId: 'price_route_pro_PLACEHOLDER',
  },
  business_route: {
    id: 'business_route',
    name: 'Business Route Pack',
    credits: 100,
    price: 20,
    stripePriceId: 'price_route_business_PLACEHOLDER',
  },
};

// Plan Types
export const PLANS = {
  FREE: 'free',
  STARTER: 'starter',
  GROWTH: 'growth',
  SCALE: 'scale',
  TRIAL: 'trial'
};

// Media Retention Policy (in days)
export const MEDIA_RETENTION = {
  FREE: 30,      // 30 days
  TRIAL: 30,     // 30 days
  STARTER: 60,   // 60 days (2 months)
  GROWTH: 180,   // 180 days (6 months)
  SCALE: 365     // 365 days (1 year)
};

// Feature Flags
export const FEATURES = {
  // Core features (Free/Trial)
  BASIC_INVOICES: 'basic_invoices',
  BASIC_ESTIMATES: 'basic_estimates',
  BASIC_CUSTOMERS: 'basic_customers',

  // Starter features
  UNLIMITED_INVOICES: 'unlimited_invoices',
  UNLIMITED_ESTIMATES: 'unlimited_estimates',
  SCHEDULING: 'scheduling',
  REMINDERS: 'reminders',
  EXPENSE_TRACKING: 'expense_tracking',
  CUSTOMER_PORTAL: 'customer_portal',
  BASIC_REPORTS: 'basic_reports',
  BASIC_AUTOMATIONS: 'basic_automations',
  EMAIL_SUPPORT: 'email_support',
  DATA_IMPORT: 'data_import', // CSV import for customers, services, etc.
  BASIC_TEAM_INVITES: 'basic_team_invites', // Invite team members up to seat limit

  // Growth features
  PUBLIC_WEBSITE: 'public_website', // Public business website with booking form
  UPTO15_SEATS: 'up_to_15_seats',
  RECURRING_JOBS: 'recurring_jobs',
  AUTOMATIONS: 'automations',
  CUSTOM_BRANDING: 'custom_branding',
  TEAM_MANAGEMENT: 'team_management',
  SMS_NOTIFICATIONS: 'sms_notifications',
  ADVANCED_SCHEDULING: 'advanced_scheduling',
  TIME_TRACKING: 'time_tracking',
  ROLE_BASED_ACCESS: 'role_based_access',
  LIVE_CHAT_SUPPORT: 'live_chat_support',
  LOCATION_TRACKING: 'location_tracking',

  // Insights features
  BASIC_INSIGHTS: 'basic_insights',             // Starter: financial insights only
  PROACTIVE_INSIGHTS: 'proactive_insights',     // Growth+: all 12 insight types

  // Messaging features
  CUSTOMER_MESSAGING: 'customer_messaging',     // Growth+: two-way customer messaging

  // Scale features
  UNLIMITED_SEATS: 'unlimited_seats',
  ADVANCED_ANALYTICS: 'advanced_analytics',
  APPROVAL_WORKFLOWS: 'approval_workflows',
  CUSTOM_DASHBOARDS: 'custom_dashboards',
  PRIORITY_SUPPORT: 'priority_support',
  SSO: 'sso',
  AUDIT_LOGS: 'audit_logs',
  CUSTOM_ROLES: 'custom_roles',
  API_ACCESS: 'api_access',
  WHITE_LABEL: 'white_label',
  AUTO_ROUTING: 'auto_routing',
  CUSTOM_AUTOMATIONS: 'custom_automations',
  CUSTOM_DOMAIN: 'custom_domain' // Custom domain for public business website
};

// Plan Limits and Features
export const PLAN_LIMITS = {
  [PLANS.FREE]: {
    name: 'Free',
    seats: 1,
    invoicesPerMonth: 5,
    estimatesPerMonth: 5,
    customersPerMonth: 10,
    storageGB: 0.5,
    mediaRetentionDays: MEDIA_RETENTION.FREE,
    features: [
      FEATURES.BASIC_INVOICES,
      FEATURES.BASIC_ESTIMATES,
      FEATURES.BASIC_CUSTOMERS
    ]
  },
  [PLANS.TRIAL]: {
    name: 'Trial',
    seats: 2,
    invoicesPerMonth: -1, // unlimited during trial
    estimatesPerMonth: -1,
    customersPerMonth: -1,
    storageGB: 5,
    mediaRetentionDays: MEDIA_RETENTION.TRIAL,
    trialDays: 15,
    features: [
      // Access to all Starter features during trial
      FEATURES.BASIC_INVOICES,
      FEATURES.BASIC_ESTIMATES,
      FEATURES.BASIC_CUSTOMERS,
      FEATURES.UNLIMITED_INVOICES,
      FEATURES.UNLIMITED_ESTIMATES,
      FEATURES.SCHEDULING,
      FEATURES.REMINDERS,
      FEATURES.EXPENSE_TRACKING,
      FEATURES.CUSTOMER_PORTAL,
      FEATURES.BASIC_REPORTS,
      FEATURES.DATA_IMPORT,
      FEATURES.BASIC_INSIGHTS,
      FEATURES.PROACTIVE_INSIGHTS
    ]
  },
  [PLANS.STARTER]: {
    name: 'Starter',
    monthlyPrice: 29,
    annualPrice: 15,
    seats: 2,
    invoicesPerMonth: -1, // unlimited
    estimatesPerMonth: -1,
    customersPerMonth: -1,
    storageGB: 10,
    mediaRetentionDays: MEDIA_RETENTION.STARTER,
    features: [
      FEATURES.BASIC_INVOICES,
      FEATURES.BASIC_ESTIMATES,
      FEATURES.BASIC_CUSTOMERS,
      FEATURES.UNLIMITED_INVOICES,
      FEATURES.UNLIMITED_ESTIMATES,
      FEATURES.SCHEDULING,
      FEATURES.REMINDERS,
      FEATURES.EXPENSE_TRACKING,
      FEATURES.CUSTOMER_PORTAL,
      FEATURES.BASIC_REPORTS,
      FEATURES.BASIC_AUTOMATIONS,
      FEATURES.EMAIL_SUPPORT,
      FEATURES.DATA_IMPORT,
      FEATURES.BASIC_TEAM_INVITES,
      FEATURES.BASIC_INSIGHTS
    ]
  },
  [PLANS.GROWTH]: {
    name: 'Growth',
    monthlyPrice: 59,
    annualPrice: 40,
    seats: 15, // Up to 15 team seats
    invoicesPerMonth: -1,
    estimatesPerMonth: -1,
    customersPerMonth: -1,
    storageGB: 50,
    mediaRetentionDays: MEDIA_RETENTION.GROWTH,
    smsPerMonth: 200,
    routeOptimizationsPerMonth: 5,
    features: [
      // All Starter features
      FEATURES.BASIC_INVOICES,
      FEATURES.BASIC_ESTIMATES,
      FEATURES.BASIC_CUSTOMERS,
      FEATURES.UNLIMITED_INVOICES,
      FEATURES.UNLIMITED_ESTIMATES,
      FEATURES.SCHEDULING,
      FEATURES.REMINDERS,
      FEATURES.EXPENSE_TRACKING,
      FEATURES.CUSTOMER_PORTAL,
      FEATURES.BASIC_REPORTS,
      FEATURES.BASIC_AUTOMATIONS,
      FEATURES.EMAIL_SUPPORT,
      FEATURES.DATA_IMPORT,
      FEATURES.BASIC_TEAM_INVITES,
      FEATURES.PUBLIC_WEBSITE,
      // Growth-specific features
      FEATURES.UPTO15_SEATS,
      FEATURES.RECURRING_JOBS,
      FEATURES.AUTOMATIONS,
      FEATURES.CUSTOM_BRANDING,
      FEATURES.TEAM_MANAGEMENT,
      FEATURES.SMS_NOTIFICATIONS,
      FEATURES.ADVANCED_SCHEDULING,
      FEATURES.TIME_TRACKING,
      FEATURES.ROLE_BASED_ACCESS,
      FEATURES.LIVE_CHAT_SUPPORT,
      FEATURES.LOCATION_TRACKING,
      FEATURES.ADVANCED_ANALYTICS, // Advanced Reporting
      FEATURES.BASIC_INSIGHTS,
      FEATURES.PROACTIVE_INSIGHTS,
      FEATURES.CUSTOMER_MESSAGING,
      FEATURES.AUTO_ROUTING
    ]
  },
  [PLANS.SCALE]: {
    name: 'Scale',
    monthlyPrice: 129,
    annualPrice: 80,
    seats: -1, // unlimited
    invoicesPerMonth: -1,
    estimatesPerMonth: -1,
    customersPerMonth: -1,
    storageGB: 200,
    mediaRetentionDays: MEDIA_RETENTION.SCALE,
    smsPerMonth: 1500,
    routeOptimizationsPerMonth: -1,
    features: [
      // All Growth features
      FEATURES.BASIC_INVOICES,
      FEATURES.BASIC_ESTIMATES,
      FEATURES.BASIC_CUSTOMERS,
      FEATURES.UNLIMITED_INVOICES,
      FEATURES.UNLIMITED_ESTIMATES,
      FEATURES.SCHEDULING,
      FEATURES.REMINDERS,
      FEATURES.EXPENSE_TRACKING,
      FEATURES.CUSTOMER_PORTAL,
      FEATURES.BASIC_REPORTS,
      FEATURES.BASIC_AUTOMATIONS,
      FEATURES.EMAIL_SUPPORT,
      FEATURES.BASIC_TEAM_INVITES,
      FEATURES.PUBLIC_WEBSITE,
      FEATURES.UPTO15_SEATS,
      FEATURES.RECURRING_JOBS,
      FEATURES.AUTOMATIONS,
      FEATURES.CUSTOM_BRANDING,
      FEATURES.TEAM_MANAGEMENT,
      FEATURES.SMS_NOTIFICATIONS,
      FEATURES.ADVANCED_SCHEDULING,
      FEATURES.TIME_TRACKING,
      FEATURES.ROLE_BASED_ACCESS,
      FEATURES.LIVE_CHAT_SUPPORT,
      FEATURES.LOCATION_TRACKING,
      FEATURES.DATA_IMPORT,
      FEATURES.ADVANCED_ANALYTICS,
      // Scale-specific features
      FEATURES.UNLIMITED_SEATS,
      FEATURES.APPROVAL_WORKFLOWS,
      FEATURES.CUSTOM_DASHBOARDS,
      FEATURES.PRIORITY_SUPPORT,
      FEATURES.SSO,
      FEATURES.AUDIT_LOGS,
      FEATURES.CUSTOM_ROLES,
      FEATURES.API_ACCESS,
      FEATURES.WHITE_LABEL,
      FEATURES.AUTO_ROUTING,
      FEATURES.CUSTOM_AUTOMATIONS,
      FEATURES.CUSTOM_DOMAIN,
      FEATURES.BASIC_INSIGHTS,
      FEATURES.PROACTIVE_INSIGHTS,
      FEATURES.CUSTOMER_MESSAGING
    ]
  }
};

// Subscription Status
export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  TRIALING: 'trialing',
  PAST_DUE: 'past_due',
  CANCELED: 'canceled',
  INCOMPLETE: 'incomplete',
  PAUSED: 'paused'
};

// Helper Functions
export function getPlanFeatures(planName) {
  return PLAN_LIMITS[planName]?.features || [];
}

export function getPlanLimits(planName) {
  return PLAN_LIMITS[planName] || PLAN_LIMITS[PLANS.FREE];
}

export function hasFeatureInPlan(planName, featureName) {
  const features = getPlanFeatures(planName);
  return features.includes(featureName);
}

export function getRequiredPlanForFeature(featureName) {
  // Returns the minimum plan required for a feature
  const planOrder = [PLANS.FREE, PLANS.STARTER, PLANS.GROWTH, PLANS.SCALE];

  for (const plan of planOrder) {
    if (hasFeatureInPlan(plan, featureName)) {
      return plan;
    }
  }

  return PLANS.SCALE; // Default to highest plan if not found
}

export function getSmsLimit(planName) {
  // Returns SMS limit for a plan (0 if not available)
  return PLAN_LIMITS[planName]?.smsPerMonth || 0;
}

export function getRouteOptimizationLimit(planName) {
  return PLAN_LIMITS[planName]?.routeOptimizationsPerMonth || 0;
}

export function getMediaRetentionDays(planName) {
  // Returns media retention days for a plan (default to 30 days)
  return PLAN_LIMITS[planName]?.mediaRetentionDays || MEDIA_RETENTION.FREE;
}

export function formatMediaRetention(days) {
  // Returns human-readable retention period
  if (days >= 365) return '1 year';
  if (days >= 180) return '6 months';
  if (days >= 90) return '3 months';
  if (days >= 60) return '2 months';
  return `${days} days`;
}

export function canUpgradeTo(currentPlan, targetPlan) {
  const planHierarchy = {
    [PLANS.FREE]: 0,
    [PLANS.TRIAL]: 0,
    [PLANS.STARTER]: 1,
    [PLANS.GROWTH]: 2,
    [PLANS.SCALE]: 3
  };

  return planHierarchy[targetPlan] > planHierarchy[currentPlan];
}

export function getPlanDisplayName(planName) {
  return PLAN_LIMITS[planName]?.name || 'Unknown';
}

export function getPlanPrice(planName, billingCycle = 'monthly') {
  const plan = PLAN_LIMITS[planName];
  if (!plan) return 0;

  return billingCycle === 'annual' ? plan.annualPrice : plan.monthlyPrice;
}

export function calculateAnnualSavings(planName) {
  const plan = PLAN_LIMITS[planName];
  if (!plan || !plan.monthlyPrice || !plan.annualPrice) return 0;

  const monthlyTotal = plan.monthlyPrice * 12;
  const annualTotal = plan.annualPrice * 12;

  return monthlyTotal - annualTotal;
}

// Feature to user-friendly name mapping
export const FEATURE_NAMES = {
  [FEATURES.BASIC_INVOICES]: 'Basic Invoices',
  [FEATURES.BASIC_ESTIMATES]: 'Basic Estimates',
  [FEATURES.BASIC_CUSTOMERS]: 'Basic Customer Management',
  [FEATURES.UNLIMITED_INVOICES]: 'Unlimited Invoices',
  [FEATURES.UNLIMITED_ESTIMATES]: 'Unlimited Estimates',
  [FEATURES.SCHEDULING]: 'Scheduling & Calendar',
  [FEATURES.REMINDERS]: 'Automated Reminders',
  [FEATURES.EXPENSE_TRACKING]: 'Expense Tracking',
  [FEATURES.CUSTOMER_PORTAL]: 'Client Portal',
  [FEATURES.BASIC_REPORTS]: 'Standard Reports',
  [FEATURES.BASIC_AUTOMATIONS]: 'Basic Automations',
  [FEATURES.EMAIL_SUPPORT]: '24h Email Support',
  [FEATURES.DATA_IMPORT]: 'CSV Data Import',
  [FEATURES.BASIC_TEAM_INVITES]: 'Team Invites (up to seat limit)',
  [FEATURES.UPTO15_SEATS]: 'Up to 15 Team Seats',
  [FEATURES.RECURRING_JOBS]: 'Recurring Jobs',
  [FEATURES.AUTOMATIONS]: 'Advanced Automations',
  [FEATURES.CUSTOM_BRANDING]: 'Custom Branding',
  [FEATURES.TEAM_MANAGEMENT]: 'Team Management',
  [FEATURES.SMS_NOTIFICATIONS]: 'SMS Notifications',
  [FEATURES.ADVANCED_SCHEDULING]: 'Calendar + SMS Scheduling',
  [FEATURES.TIME_TRACKING]: 'Time Tracking',
  [FEATURES.ROLE_BASED_ACCESS]: 'Role-Based Access Control',
  [FEATURES.LIVE_CHAT_SUPPORT]: 'Live Chat Support',
  [FEATURES.UNLIMITED_SEATS]: 'Unlimited Team Seats',
  [FEATURES.ADVANCED_ANALYTICS]: 'Advanced Reports',
  [FEATURES.APPROVAL_WORKFLOWS]: 'Approval Workflows',
  [FEATURES.CUSTOM_DASHBOARDS]: 'Enterprise Dashboards',
  [FEATURES.PRIORITY_SUPPORT]: '24/7 Priority Support',
  [FEATURES.SSO]: 'Single Sign-On (SSO)',
  [FEATURES.AUDIT_LOGS]: 'Audit Logs',
  [FEATURES.CUSTOM_ROLES]: 'Custom Roles & Permissions',
  [FEATURES.API_ACCESS]: 'API Access',
  [FEATURES.WHITE_LABEL]: 'White Label',
  [FEATURES.AUTO_ROUTING]: 'Route Optimization',
  [FEATURES.CUSTOM_AUTOMATIONS]: 'Custom Automations',
  [FEATURES.LOCATION_TRACKING]: 'Real-Time Location Tracking',
  [FEATURES.PUBLIC_WEBSITE]: 'Public Business Website',
  [FEATURES.CUSTOM_DOMAIN]: 'Custom Domain for Website',
  [FEATURES.BASIC_INSIGHTS]: 'Basic Business Insights',
  [FEATURES.PROACTIVE_INSIGHTS]: 'Proactive AI Insights',
  [FEATURES.CUSTOMER_MESSAGING]: 'Customer Messaging'
};

export function getFeatureDisplayName(featureName) {
  return FEATURE_NAMES[featureName] || featureName;
}
